import { AutoComplete, ListItem, Listbox } from "../../styles/global.style";
import { ClickAwayListener, FormControl, InputAdornment, InputLabel, Typography, styled } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import React, { ChangeEvent, useEffect, useState } from "react";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

import { Box } from "@mui/system";
import { GvTextField } from "../../styles/styledComponents";
import { IOptionGeneric } from "../../shared/interface";
import { InfoIcon } from "../Icons";
import { Loading } from "..";
import { paletteColors } from "../../styles/muiTheme";
import { useBreeds } from "../../services/breed";
import { useDebounce } from "../../hooks";

interface IBreedSearchAutocompleteProps {
  id?: string;
  name: string;
  control: Control<any>;
  specieId: string;
  text: string;
  onSelect?: (breed: IOptionGeneric) => void;
  onChange?: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  tooltip?: string;
  breedWarning?: boolean;
}

const GvBreedSearchAutocomplete = ({
  id,
  name,
  control,
  specieId,
  text,
  onSelect,
  onChange,
  placeholder,
  disabled,
  tooltip,
  breedWarning,
}: IBreedSearchAutocompleteProps) => {
  const [showPrediction, setShowPrediction] = useState(false);
  const [search, setSearch] = useState<string>("");
  const debouncedValue = useDebounce<string>(search);
  const { searchBreed, loading, breeds } = useBreeds();
  const [open, setOpen] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const filteredValue = event.target.value.replace(/[^a-zA-Z0-9\s-]/g, "");

    onChange && onChange(filteredValue);
    setSearch(filteredValue);
  };

  const handleSelectPlace = (breed: any) => {
    setSearch(breed.label);
    onSelect && onSelect(breed);
    setShowPrediction(false);
  };

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: paletteColors.sand.main,
      color: paletteColors.black.main,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "16px",
      fontFamily: "geograph-regular",
      padding: ".5rem 1rem",
      border: `1px solid ${paletteColors.taupe.dark}`,
      top: ".5rem",
      left: "-1.5rem",
      position: "absolute",
      width: "265px",
      p: {
        fontSize: "12px",
      },
    },
  });

  useEffect(() => {
    setSearch(text);
  }, [text]);

  useEffect(() => {
    searchBreed(specieId, debouncedValue);
  }, [debouncedValue]);

  return (
    <AutoComplete>
      <FormControl fullWidth={true}>
        <InputLabel htmlFor={id} className="font-semibold" sx={{ display: "flex", alignItems: "center" }}>
          Breed

          {tooltip && (
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <HtmlTooltip
                title={
                  <Typography color="inherit">{tooltip}</Typography>
                }
                placement="right"
                open={open}
              >
                <Box
                  sx={{ display: "grid", ml: .5 }}
                  className={`${disabled ?"opacity-35":""}`}
                  onMouseEnter={() => setOpen(true)}
                  onMouseLeave={() => setOpen(false)}
                  onTouchStart={() => setOpen(true)}
                >
                  <InfoIcon width={18} height={18} />
                </Box>
              </HtmlTooltip>
            </ClickAwayListener>
          )}
        </InputLabel>
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState }) =>
            <GvTextField
              id={id}
              type="text"
              placeholder={placeholder ? placeholder : "Enter breed"}
              onFocus={() => {
                setShowPrediction(true);
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">
                  {loading && <Loading size={26} />}
                </InputAdornment>,
              }}
              disabled={disabled}
              autoComplete="off"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              {...field}
              value={search}
              onChange={handleChange}
            />
          }
        />

        {showPrediction && breeds?.length > 0 ? (
          <Listbox>
            {breeds.map((breed) => (
              <ListItem key={breed.value} onClick={() => handleSelectPlace(breed)}>{breed.label}</ListItem>
            ))}
          </Listbox>
        ) : null}

        {breedWarning && (
          <Box className="rounded bg-[#F9EED5] p-2.5 pr-4 relative
          top-2 font-geograph-regular text-sm text-[#262626] mt-2 mb-5">
              Choose &quot;other&quot; for unlisted breeds or &quot;unknown&quot; if unsure.
          </Box>
        )}
      </FormControl>
    </AutoComplete>
  );
};

export default GvBreedSearchAutocomplete;
